/// <amd-module name="Environments/EnvironmentDetails/Components/EnvironmentDeploymentsHistoryComponent" />
import React = require("react");

import { TaskResult } from "DistributedTask/Client/Contracts/TaskAgent";

import { IEnvironmentDeploymentJobStatus } from "Environments/Client/EnvironmentContracts";
import { EnvironmentDeploymentsHistoryActionsCreator } from "Environments/EnvironmentDetails/ActionsCreators/EnvironmentDeploymentsHistoryActionsCreator";
import { PageKey, PerformanceScenarios } from "Environments/EnvironmentDetails/Common/Utils";
import { DeploymentsHistoryFilterBar, IDeploymentHistoryFilterState } from "Environments/EnvironmentDetails/Components/DeploymentsHistoryFilterBar";
import * as Resources from "Environments/EnvironmentDetails/Resources";
import { EnvironmentDeploymentsHistoryStore, IEnvironmentDeploymentRecord } from "Environments/EnvironmentDetails/Stores/EnvironmentDeploymentsHistoryStore";
import { EnvironmentStore } from "Environments/EnvironmentDetails/Stores/EnvironmentStore";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { getEnvironmentsRunDetailsPageUrl } from "Environments/EnvironmentsCommon/Link";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { StoreManager } from "Environments/Flux/StoreManager";

import { Action, Area, FeatureNames } from "Environments/EnvironmentDetails/Common/Utils";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format, localeFormat } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { Card, ICardContentProps } from "VSSUI/Card";
import { Duration } from "VSSUI/Duration";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableBreakpoint, ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { Filter } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

interface ITableItem extends IEnvironmentDeploymentRecord {
}

interface IState extends IEnvironmentBaseComponentState {
    itemProvider: ArrayItemProvider<ITableItem>;
    pipelineDefinitionIdFilter?: number,
    resourceIdFilter?: number,
    statusFilter?: TaskResult,
    showZeroDataExperience: boolean;
}

interface IProps extends IEnvironmentBaseComponentProperties {
    instanceId: string;
    environmentId: number;
    markTTICallback: (scenario: string) => void;
    filter: Filter;
    filterToggled: ObservableValue<boolean>;
    setFilterBarIconVisibility: (visible: boolean) => void;
    addResourceButtonCallBack: () => void;
}

export class EnvironmentDeploymentsHistoryComponent extends EnvironmentBaseComponent<IProps, IState> {

    public getInstanceId(context: IVssComponentContext) : string {
        return this.props.instanceId;
    }

    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);

        this._actionCreator = ActionCreatorManager.GetActionCreator<EnvironmentDeploymentsHistoryActionsCreator>(EnvironmentDeploymentsHistoryActionsCreator, this._instanceId);
        this._store = StoreManager.GetStore<EnvironmentDeploymentsHistoryStore>(EnvironmentDeploymentsHistoryStore, this._instanceId);
        this._environmentStore = StoreManager.GetStore<EnvironmentStore>(EnvironmentStore, props.instanceId);

        publishTelemetry(context.pageContext, Area, FeatureNames.EnvironmentView, { "Action": Action.DeploymentTabNavigation, "EnvironmentId": props.environmentId.toString()});

        const records = this._store.getDeploymentRecords();
        this.state = { 
            itemProvider: new ArrayItemProvider<ITableItem>(records),
            errorMessages: {},
            showZeroDataExperience: records.length === 0
        }; 

        this._store.addChangedListener(this._setState);
    }

    public componentDidMount(): void {
        this.props.markTTICallback(PerformanceScenarios.EnvironmentDeploymentsPageLoad);
        super.componentDidMount();
    }
    
    public render(): JSX.Element {

        if (this.state.showZeroDataExperience && !this.props.filter.hasChangesToReset()) {
            return this._getZeroDataContent();
        }

        
        return (
            <div className="page-content page-content-top">
                {this._renderFilterBar()}
                {this.getErrorMessageView(PageKey.EnvironmentDeploymentHistory)}
                {this._getDeploymentView()}
            </div>
        );
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._setState);
        super.componentWillUnmount();
    }

    private _getDeploymentView(): JSX.Element {
        if(this.state.showZeroDataExperience && this.props.filter.hasChangesToReset()) {
            return this._getZeroDataContentForFilters();
        } else {
            const itemProvider: ArrayItemProvider<ITableItem> = this.state.itemProvider;
            const cardContentProps: ICardContentProps = { contentPadding: false };
            return (
                <>
                <Card
                    className="bolt-card-no-vertical-padding env-filter-spacer"
                    contentProps={cardContentProps}>
                    <div>
                    <Table<Partial<ITableItem>>
                        ariaLabel={Resources.Deployments}
                        key={"env-dep-history-table"}
                        columns={this._sizableColumns}
                        itemProvider={itemProvider}
                        tableBreakpoints={this._getTableBreakPoints()}
                        onActivate={this._onRowClicked}/>
                    {this._getShowMoreElement()}
                    </div>
                </Card>
                <Card
                    className="bolt-card-no-vertical-padding"
                    contentProps={cardContentProps}>
                        {this._getZeroDataContentForResource()}
                </Card>
                </>
            );
        }
    }

    private _renderFilterBar(): JSX.Element {
        return (
        <DeploymentsHistoryFilterBar
            instanceId={this._instanceId}
            environmentId={this.props.environmentId}
            pageContext={this.context.pageContext}
            filter={this.props.filter}
            filterToggled={this.props.filterToggled}
            onFiltersUpdated={this._onFilterUpdated}/>);
    }
    
    private _onFilterUpdated = (filterState: IDeploymentHistoryFilterState): void =>  {
       const resourceFilter: number | undefined = !!filterState.resource ? filterState.resource.id : undefined;

       this.setState({
            resourceIdFilter:  resourceFilter,
            pipelineDefinitionIdFilter: filterState.pipelineDefinitionId,
            statusFilter: filterState.status,
        });

        this._getDeploymentExecutionRecords(resourceFilter, filterState.pipelineDefinitionId, filterState.status, true);
    }

    private _getZeroDataContent(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environment-details/Images/no-deployments.svg");
        return (
            <div className="zero-data-container">
                <ZeroData
                    primaryText={Resources.ZeroDataDeploymentsPrimaryText}
                    imagePath={image}
                    imageAltText={Resources.ZeroDataDeploymentsImageAltText}
                    secondaryText={Resources.ZeroDataDeploymentsSecondaryText}
                    className={"zero-data-content"}
                />
            </div>
        )
    }

    private _getZeroDataContentForResource(): JSX.Element | undefined {
        if(this._environmentStore.getResources().length === 0) {
            const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environment-details/Images/no-deployments.svg");
            return (
                <div className="zero-data-container">
                    <ZeroData
                        primaryText={Resources.ZeroDataDeploymentsPrimaryText}
                        imagePath={image}
                        imageAltText={Resources.ZeroDataDeploymentsImageAltText}
                        secondaryText={Resources.ZeroDataResourcesSecondaryTextAddResource}
                        className={"zero-data-content"}
                        actionText={Resources.AddResource}
                        actionType={ZeroDataActionType.ctaButton}
                        onActionClick={this.props.addResourceButtonCallBack}
                    />
                </div>
            )
        }
    }

    private _getShowMoreElement(): React.ReactNode {
        const continuationToken = this._store.getContinuationToken();
        if (!!continuationToken) {
            return (
                <div className={"show-more-button"}>
                    <Button
                        onClick={() => {
                            this._actionCreator.fetchMoreEnvironmentDeploymentExecutionRecords(
                                this.context.pageContext,
                                this.props.environmentId,
                                this.state.resourceIdFilter,
                                this.state.pipelineDefinitionIdFilter,
                                this.state.statusFilter,
                                continuationToken
                            );
                        }}>
                        {Resources.ShowMoreButtonText}
                    </Button>
                </div>
            );
        }
        
        return null;
    }

    private _getDeploymentExecutionRecords(
        resourceId: number | undefined = undefined,
        pipelineDefinitionId: number | undefined = undefined,
        status: TaskResult | undefined = undefined,
        forceRefresh: boolean = true): boolean {

        return this._actionCreator.getEnvironmentDeploymentExecutionRecords(
            this.context.pageContext,
            this.props.environmentId,
            resourceId,
            pipelineDefinitionId,
            status,
            forceRefresh);
    }

    private _setState = (): void  => {
        const records: IEnvironmentDeploymentRecord[] = this._store.getDeploymentRecords();
        const itemProvider = new ArrayItemProvider<ITableItem>(records);
        this.props.setFilterBarIconVisibility(!this._shouldShowZeroDataPage() || this.props.filter.hasChangesToReset());
        this.setState({
            itemProvider: itemProvider,
            showZeroDataExperience: this._shouldShowZeroDataPage()
        }, () => {
            const resultCount = this.state.itemProvider.length;
            announce(resultCount > 0 ? format(Resources.FilteredDataCountText, resultCount) : Resources.NoResultsFound);
        });
    }

    private _getZeroDataContentForFilters(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-environments-web/environment-details/Images/no-results.png");
        return (
            <Card className="flex-grow bolt-card-no-vertical-padding environment-minimum-card-height" contentProps={{ contentPadding: false }}>
                <div className="zero-data-container">
                    <ZeroData
                        imageAltText={Resources.NoResultsFound}
                        imagePath={image}
                        secondaryText={Resources.NoResultsFound}
                        className={"zero-data-content"}
                    />
                </div>
            </Card>
        )
    }

    private _shouldShowZeroDataPage(): boolean {
        return (
            this._store.getDeploymentRecords().length === 0 &&
            !this._messagesStore.getMessage(PageKey.EnvironmentDeploymentHistory)
        );
    }

    private _onRowClicked = (event: any, row: ITableRow<ITableItem>): void => {

        const tableItem: ITableItem = row.data;
        const environmentId = this.props.environmentId;
        const environmentExecutionRecordId = tableItem.maxJobRecordId;
        const ownerId = tableItem.ownerId;
        const definitionId = tableItem.definitionId;
        const planType = tableItem.planType;

        onClickFPS(this.context.pageContext, getEnvironmentsRunDetailsPageUrl(this.context.pageContext, environmentId, environmentExecutionRecordId, ownerId, definitionId, planType), true);
    }

    private _renderRunsColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <TwoLineTableCell
                className="bolt-table-cell-content-with-inline-link"
                key={"runs-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={
                    <Tooltip overflowOnly={true} text={tableItem.ownerSourceInfoMessage}>
                        <span className={"bolt-table-inline-link-left-padding font-weight-semibold body-m text-ellipsis"}>{tableItem.ownerSourceInfoMessage}</span>
                    </Tooltip>
                }
                line2={
                    <Link
                        className="body-s text-ellipsis bolt-table-link bolt-table-inline-link"
                        excludeTabStop={true}
                        title={tableItem.ownerName}
                        href={tableItem.ownerUrl}
                        tooltipProps={{overflowOnly: true}}
                    >
                        {localeFormat(Resources.DeploymentsSecondaryText, tableItem.ownerName, tableItem.definitionName)}
                    </Link>
                }
            />
        );
    }

    private _renderJobsColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const statusProps = this._getStatus(tableItem.status);     
        return (
            <SimpleTableCell
                key={"jobs-col" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                ariaLabel={"jobs-col" + columnIndex}>
                <Status {...statusProps} className="environment-icon-margin" size={StatusSize.m} />
                <Tooltip overflowOnly={true} text={tableItem.jobsInfo}>
                    <span className={"bolt-table-inline-link-left-padding font-weight-semibold body-m text-ellipsis"}>{tableItem.jobsInfo}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _getStatus(status: IEnvironmentDeploymentJobStatus | undefined): IStatusProps {
        switch(status) {
            case IEnvironmentDeploymentJobStatus.Canceled:
                return Statuses.Canceled;
            case IEnvironmentDeploymentJobStatus.Failed:
                return Statuses.Failed;
            case IEnvironmentDeploymentJobStatus.Inprogress:
                return Statuses.Running;
            case IEnvironmentDeploymentJobStatus.NotStarted:
                return Statuses.Waiting;
            case IEnvironmentDeploymentJobStatus.Succeeded:
                return Statuses.Success;
            case IEnvironmentDeploymentJobStatus.SucceededWithIssues:
                return Statuses.Warning;
        }

        return Statuses.Waiting;
    }

    private _renderCompletedOnColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const { queueTime, finishTime } = tableItem

        const dateLine = (
            <div className="flex-row flex-center font-size">
                <Icon iconName="Calendar" className="environments-time-icon" />
                { queueTime && <Ago date={queueTime} format={AgoFormat.Compact} /> }
            </div>
        );

        const durationLine = (
            <div className="flex-row flex-center font-size bolt-table-two-line-cell-item">
                <Icon iconName="Clock" className="environments-time-icon" />
                { queueTime && <Duration startDate={queueTime} endDate={finishTime} /> }
            </div>
        );

        return (
            <TwoLineTableCell
                key={"completedOn-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                line1={dateLine}
                line2={durationLine}
            />
        );
    }

    private _onSizeSizable = (event: MouseEvent, index: number, width: number): void => {
        (this._sizableColumns[index].width as ObservableValue<number>).value = width;
    }

    private _getTableBreakPoints(): ITableBreakpoint[] {
        var breakpoints: ITableBreakpoint[];

        breakpoints = [
            {
                breakpoint: ScreenBreakpoints.xsmall,
                columnWidths: [-125, -125, 100]
            },
            {
                breakpoint: ScreenBreakpoints.small,
                columnWidths: [-200, -200, 100]
            },
            {
                breakpoint: ScreenBreakpoints.small,
                columnWidths: [-300, -300, 100]
            }
        ];

        return breakpoints;
    }

    private _sizableColumns = [
        { 
            id: "pipeline-col",
            name: Resources.Run,
            width: new ObservableValue(-300),
            className: "environment-cell",
            renderCell: this._renderRunsColumn,
            onSize: this._onSizeSizable 
        },
        { 
            id: "jobs-col",
            name: Resources.RunsJobsColumnName,
            width: new ObservableValue(-300),
            className: "environment-cell",
            renderCell: this._renderJobsColumn,
            headerClassName: "env-header",
            onSize: this._onSizeSizable 
        },
        { 
            id: "completedon-col",
            name: "",
            width: new ObservableValue(100),
            className: "environment-cell",
            renderCell: this._renderCompletedOnColumn,
            onSize: this._onSizeSizable 
        }
    ];

    private _store: EnvironmentDeploymentsHistoryStore;
    private _actionCreator: EnvironmentDeploymentsHistoryActionsCreator;
    private _environmentStore: EnvironmentStore;
}
